import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField,
  Alert, CircularProgress, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, IconButton
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Sidebar, { SIDEBAR_WIDTH } from '../components/Sidebar';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const isMobile = useIsMobile();
  const [totpSetupOpen, setTotpSetupOpen] = useState(false);
  const [totpData, setTotpData] = useState(null);
  const [totpVerifyCode, setTotpVerifyCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [photoLoading, setPhotoLoading] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [pwForm, setPwForm] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const fileInputRef = React.useRef();

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase()
    || user?.username?.[0]?.toUpperCase();

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Photo must be under 5MB.');
      return;
    }
    setPhotoLoading(true);
    setPhotoError('');
    const formData = new FormData();
    formData.append('profile_photo', file);
    try {
      await api.post('/auth/upload-photo/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshUser();
    } catch {
      setPhotoError('Photo upload failed. Please try again.');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleSetupTOTP = async () => {
    setMfaLoading(true);
    try {
      const { data } = await api.get('/auth/totp-setup/');
      setTotpData(data);
      setTotpVerifyCode('');
      setTotpError('');
      setTotpSetupOpen(true);
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to initialize Google Authenticator setup.' });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyTOTPSetup = async () => {
    setMfaLoading(true);
    setTotpError('');
    try {
      const { data } = await api.post('/auth/totp-verify-setup/', { code: totpVerifyCode });
      await refreshUser();
      setTotpSetupOpen(false);
      setMsg({ type: 'success', text: data.message });
    } catch (err) {
      setTotpError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleToggleMFA = async (type = null) => {
    setMfaLoading(true);
    try {
      const { data } = await api.post('/auth/toggle-mfa/', type ? { mfa_type: type } : {});
      if (data.requires_setup) {
        await handleSetupTOTP();
      } else {
        await refreshUser();
        setMsg({ type: 'success', text: data.message });
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update MFA.' });
    } finally {
      setMfaLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('New passwords do not match.');
      return;
    }
    setPwLoading(true);
    try {
      await api.post('/auth/change-password/', pwForm);
      setPwSuccess('Password changed successfully.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwError(err.response?.data?.error || 'Password change failed.');
    } finally {
      setPwLoading(false);
    }
  };

  const pwFields = [
    { field: 'current_password', label: 'Current password', show: showCurrentPw, toggle: () => setShowCurrentPw(!showCurrentPw) },
    { field: 'new_password', label: 'New password', show: showNewPw, toggle: () => setShowNewPw(!showNewPw) },
    { field: 'confirm_password', label: 'Confirm new password', show: showConfirmPw, toggle: () => setShowConfirmPw(!showConfirmPw) },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f4f6f4' }}>
      <Sidebar />
      <Box sx={{
        ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
        p: isMobile ? 2 : 3,
        pt: isMobile ? 7 : 3,
        minHeight: '100vh',
        flex: 1,
        minWidth: 0,
        overflowX: 'hidden',
      }}>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>My Profile</Typography>
          <Typography sx={{ color: '#888', fontSize: 13, mt: 0.5 }}>{today}</Typography>
        </Box>

        {msg.text && (
          <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

          {/* Profile info card */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Card>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Profile information</Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>

                {/* Avatar */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ position: 'relative', mb: 1.5 }}>
                    {user?.profile_photo_url ? (
                      <Box
                        component="img"
                        src={user.profile_photo_url}
                        alt="Profile"
                        sx={{
                          width: 88, height: 88, borderRadius: '50%',
                          objectFit: 'cover', border: '3px solid #eaf3de'
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: 88, height: 88, borderRadius: '50%',
                        background: '#eaf3de', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 28, fontWeight: 500, color: '#1a4d2e',
                        border: '3px solid #d0e8d0',
                      }}>
                        {initials}
                      </Box>
                    )}
                  </Box>

                  <input
                    type="file" accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handlePhotoChange}
                  />
                  <Button
                    size="small" variant="outlined"
                    onClick={() => fileInputRef.current.click()}
                    disabled={photoLoading}
                    sx={{ fontSize: 12, borderColor: '#2d7a4f', color: '#1a4d2e', borderRadius: 2 }}
                  >
                    {photoLoading ? <CircularProgress size={14} /> : 'Change photo'}
                  </Button>
                  {photoError && (
                    <Typography sx={{ fontSize: 12, color: 'red', mt: 1 }}>{photoError}</Typography>
                  )}
                  <Typography sx={{ fontSize: 11, color: '#bbb', mt: 0.5 }}>JPG or PNG, max 5MB</Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {[
                  { label: 'Full name', value: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '—' },
                  { label: 'Username', value: `@${user?.username}` },
                  { label: 'Email', value: user?.email || '—' },
                  { label: 'Phone', value: user?.phone || '—' },
                  { label: 'Department', value: user?.department || '—' },
                  { label: 'State of assignment', value: user?.state_of_assignment || '—' },
                  { label: 'Role', value: user?.role === 'department_head' ? 'Department Head' : user?.role === 'admin' ? 'Administrator' : 'Employee' },
                ].map(({ label, value }) => (
                  <Box key={label} sx={{
                    display: 'flex', justifyContent: 'space-between',
                    py: 1, borderBottom: '0.5px solid #f0f4f0'
                  }}>
                    <Typography sx={{ fontSize: 13, color: '#888' }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>

            {/* MFA card */}
            <Card>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Two-Factor Authentication</Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{
                  p: 2, borderRadius: 2,
                  background: user?.mfa_enabled ? '#f0f8f0' : '#fafafa',
                  border: `0.5px solid ${user?.mfa_enabled ? '#2d7a4f' : '#e0e8e0'}`,
                  mb: 2
                }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: user?.mfa_enabled ? '#1a4d2e' : '#444' }}>
                    MFA is currently {user?.mfa_enabled ? `enabled (${user?.mfa_type === 'totp' ? 'Google Authenticator' : 'Email OTP'}) ✅` : 'disabled'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>
                    {user?.mfa_enabled
                      ? 'You are using Google Authenticator for two-factor authentication.'
                      : 'Enable MFA to add an extra layer of security.'}
                  </Typography>
                </Box>

                {!user?.mfa_enabled && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleSetupTOTP()}
                    disabled={mfaLoading}
                    sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' }, borderRadius: 2, mb: 2 }}
                  >
                    {mfaLoading ? <CircularProgress size={18} color="inherit" /> : '📱 Enable with Google Authenticator'}
                  </Button>
                )}

                {user?.mfa_enabled && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleToggleMFA()}
                    disabled={mfaLoading}
                    sx={{ borderColor: '#791f1f', color: '#791f1f', borderRadius: 2 }}
                  >
                    {mfaLoading ? <CircularProgress size={18} color="inherit" /> : 'Disable MFA'}
                  </Button>
                )}

                {/* TOTP Setup Dialog */}
                <Dialog open={totpSetupOpen} onClose={() => setTotpSetupOpen(false)} maxWidth="xs" fullWidth>
                  <DialogTitle sx={{ fontWeight: 500 }}>Set up Google Authenticator</DialogTitle>
                  <DialogContent>
                    {totpData && (
                      <Box>
                        <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>
                          Open Google Authenticator on your phone and either scan the QR code or enter the setup key manually.
                        </Typography>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                          <img src={totpData.qr_code} alt="QR Code" style={{ width: 180, height: 180 }} />
                        </Box>
                        <Box sx={{ p: 1.5, background: '#f5f5f5', borderRadius: 2, mb: 2 }}>
                          <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5, fontWeight: 500 }}>
                            Manual setup key:
                          </Typography>
                          <Typography sx={{
                            fontSize: 16, fontWeight: 500, letterSpacing: '0.15em',
                            fontFamily: 'monospace', color: '#1a4d2e', wordBreak: 'break-all'
                          }}>
                            {totpData.secret}
                          </Typography>
                          <Button
                            size="small"
                            sx={{ mt: 0.5, fontSize: 11, color: '#2d7a4f', p: 0 }}
                            onClick={() => navigator.clipboard.writeText(totpData.secret)}
                          >
                            Copy key
                          </Button>
                        </Box>
                        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>
                          Enter the 6-digit code from the app to confirm setup:
                        </Typography>
                        <TextField
                          label="6-digit verification code"
                          fullWidth
                          value={totpVerifyCode}
                          onChange={(e) => setTotpVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          inputProps={{ maxLength: 6, style: { letterSpacing: '0.4em', fontSize: 20, textAlign: 'center' } }}
                        />
                        {totpError && (
                          <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, fontSize: 13 }}>{totpError}</Alert>
                        )}
                      </Box>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setTotpSetupOpen(false)} sx={{ color: '#888' }}>Cancel</Button>
                    <Button
                      variant="contained"
                      onClick={handleVerifyTOTPSetup}
                      disabled={totpVerifyCode.length !== 6 || mfaLoading}
                      sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
                    >
                      {mfaLoading ? <CircularProgress size={16} color="inherit" /> : 'Verify & activate'}
                    </Button>
                  </DialogActions>
                </Dialog>

                <Box sx={{ mt: 2, p: 1.5, background: '#fafcfa', borderRadius: 2, border: '0.5px solid #e0e8e0' }}>
                  <Typography sx={{ fontSize: 12, color: '#888', fontWeight: 500, mb: 0.5 }}>How MFA works</Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>• Google Authenticator works offline — no internet needed</Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>• Install Google Authenticator from your app store</Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>• Each code is valid for 30 seconds only</Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>• Use the manual key if QR code scanning is not possible</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Change password card */}
          <Card sx={{ alignSelf: 'start' }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Change password</Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {pwError && (
                <Alert severity="error" sx={{ mb: 2, fontSize: 13, borderRadius: 2 }}>{pwError}</Alert>
              )}
              {pwSuccess && (
                <Alert severity="success" sx={{ mb: 2, fontSize: 13, borderRadius: 2 }}>{pwSuccess}</Alert>
              )}
              <form onSubmit={handlePasswordChange}>
                {pwFields.map(({ field, label, show, toggle }) => (
                  <TextField
                    key={field}
                    label={label}
                    type={show ? 'text' : 'password'}
                    fullWidth
                    margin="dense"
                    required
                    value={pwForm[field]}
                    onChange={(e) => setPwForm({ ...pwForm, [field]: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={toggle} edge="end" size="small">
                            {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                ))}
                <Button
                  type="submit" variant="contained" fullWidth
                  disabled={pwLoading}
                  sx={{ mt: 2, background: '#1a4d2e', '&:hover': { background: '#2d7a4f' }, borderRadius: 2 }}
                >
                  {pwLoading ? <CircularProgress size={18} color="inherit" /> : 'Update password'}
                </Button>
              </form>

              <Box sx={{ mt: 2.5, p: 1.5, background: '#fafcfa', borderRadius: 2, border: '0.5px solid #e0e8e0' }}>
                <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5, fontWeight: 500 }}>
                  Password requirements
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#aaa' }}>• At least 8 characters</Typography>
                <Typography sx={{ fontSize: 12, color: '#aaa' }}>• Mix of letters and numbers recommended</Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}